/** Set up Slack buttons that answer approvals in place: a Slack app's Request URL and signing secret. */
import { Copy } from '@orgus/ui/icons';
import { useState } from 'react';
import type { ChannelView } from '@orgus/protocol';
import { Button, Dialog, ErrorState, IconButton, Input, toast, toUiError } from '@orgus/ui';
import { patchChannel } from './api.ts';

export function SlackButtonsDialog({ open, onOpenChange, channel, publicUrl, onSaved }: { open: boolean; onOpenChange: (o: boolean) => void; channel: ChannelView; publicUrl: string | null; onSaved: () => void }) {
  const [secret, setSecret] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const requestUrl = publicUrl ? `${publicUrl}/api/notify/slack/${channel.id}` : null;
  const save = async (value: string | null) => {
    setBusy(true);
    setError(null);
    try {
      await patchChannel(channel.id, { signingSecret: value });
      toast.success(value ? 'Slack buttons are on' : 'Slack buttons are off', channel.name);
      setSecret('');
      onOpenChange(false);
      onSaved();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Answer from Slack"
      description="Approve and Deny buttons in Slack answer in place, as the person who pressed them."
      size="lg"
      dismissible={!busy}
      footer={
        <>
          {channel.interactive && (
            <Button variant="ghost" onClick={() => void save(null)} disabled={busy}>
              Turn off
            </Button>
          )}
          <Button variant="primary" loading={busy} disabled={!secret.trim() || !requestUrl} onClick={() => void save(secret.trim())}>
            Save signing secret
          </Button>
        </>
      }
    >
      <ol className="flex flex-col gap-4 text-ui text-fg">
        <li className="flex gap-3"><span className="num flex size-5 shrink-0 items-center justify-center rounded-full bg-raised text-caption font-semibold text-fg-muted">1</span><div className="flex min-w-0 flex-1 flex-col gap-2">
          In Slack, open the app that owns this webhook (api.slack.com/apps), then Interactivity and Shortcuts, and turn it on.
        </div></li>
        <li className="flex gap-3"><span className="num flex size-5 shrink-0 items-center justify-center rounded-full bg-raised text-caption font-semibold text-fg-muted">2</span><div className="flex min-w-0 flex-1 flex-col gap-2">
          <span>Set the Request URL to:</span>
          {requestUrl ? (
            <span className="flex items-center gap-2 rounded-control border border-border bg-raised px-2 py-2 font-mono text-caption text-fg">
              <span className="min-w-0 flex-1 truncate">{requestUrl}</span>
              <IconButton
                size="xs"
                label="Copy the Request URL"
                icon={<Copy />}
                onClick={() => void navigator.clipboard?.writeText(requestUrl).then(() => toast.success('Copied'), () => toast.error('Could not copy. Select the address and copy it.'))}
              />
            </span>
          ) : (
            <ErrorState
              variant="inline"
              error={{ what: 'Slack cannot reach this hub yet.', why: 'The hub has no public address for Slack to call.', next: 'Set the public address under Rules first (for example a Tailscale Funnel https address).' }}
            />
          )}
        </div></li>
        <li className="flex gap-3"><span className="num flex size-5 shrink-0 items-center justify-center rounded-full bg-raised text-caption font-semibold text-fg-muted">3</span><div className="flex min-w-0 flex-1 flex-col gap-2">
          <span>Paste the Signing Secret from Basic Information.</span>
          <Input label="Signing secret" type="password" autoComplete="off" value={secret} onChange={(e) => setSecret(e.target.value)} placeholder={channel.interactive ? 'Saved. Paste to replace it.' : ''} />
        </div></li>
        <li className="flex gap-3"><span className="num flex size-5 shrink-0 items-center justify-center rounded-full bg-raised text-caption font-semibold text-fg-muted">4</span><div className="flex min-w-0 flex-1 flex-col gap-2">Each person links their Slack member ID in Settings, On call.</div></li>
      </ol>
      {error != null && <ErrorState className="mt-3" variant="inline" error={toUiError(error)} />}
    </Dialog>
  );
}
